import { UserFormData } from '../components/UserForm/hooks/useUserFormValidation';
import { UserFormChangedFields } from '../components/UserForm/utils/changedFields';
import { UpdateUserInput } from '../hooks/useUpdateUser';

const isEmailLoginMethod = (
  loginMethod: UserFormData['loginMethod']
): boolean => loginMethod === 'email' || loginMethod === 'emailAndPhone';

/**
 * Builds a `userUpdate` payload carrying only the fields the admin edited.
 *
 * Re-sending an untouched field is not free — the backend re-validates whatever
 * it receives. Email-login users have no stored phone but are served a shared
 * placeholder (`+0000000000`) on read, so echoing that value back looked like a
 * phone change and failed the duplicate-phone check for every one of them
 * (CRM-362).
 */
export const buildUserUpdateInput = ({
  userId,
  values,
  changedFields,
}: {
  userId: string;
  values: UserFormData;
  changedFields: UserFormChangedFields;
}): UpdateUserInput => ({
  id: userId,
  ...(changedFields.name && { name: values.name }),
  // For email-login users the form's email field holds the login credential,
  // so it travels as loginEmail; otherwise it is a plain contact address.
  ...(changedFields.email &&
    (isEmailLoginMethod(values.loginMethod)
      ? { loginEmail: values.email }
      : { email: values.email })),
  ...(changedFields.phone && { phone: values.phone }),
  ...(changedFields.locale && { locale: values.locale }),
  ...(changedFields.isTest && { isTest: values.isTest ?? false }),
  ...(changedFields.supplier && { supplier: values.supplier ?? false }),
  ...(changedFields.supportLogin && {
    supportLogin: values.supportLogin ?? false,
  }),
  ...(changedFields.orderPreferences && {
    orderPreferences: values.orderPreferences,
  }),
});
